import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { StorageItem } from '../../../services/storageService';
import { colors, spacing, breakpoints } from '../../../constants/theme';
import { parseFlashCheckFileName, formatRelativeDate } from '../../../utils/fileUtils';
import {
  ContentPadding,
  Grid,
  Card,
  CardTitle,
  CardDescription,
  Badge,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Input
} from './styled';

// Enhanced LibraryTab styled components
const LibraryHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};

  @media (max-width: ${breakpoints.tablet}) {
    gap: ${spacing.xs};
    margin-bottom: ${spacing.sm};
  }
`;

const SearchAndFilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};

  @media (min-width: ${breakpoints.tablet}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    gap: ${spacing.xs};
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;

  @media (max-width: ${breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const SearchInput = styled(Input)`
  padding-left: 40px;
  font-size: 14px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
  font-size: 16px;
`;

const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  font-size: 14px;
  color: ${colors.text.secondary};
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.tablet}) {
    justify-content: space-between;
    gap: ${spacing.sm};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 12px;
    gap: ${spacing.xs};
    justify-content: flex-start;
  }
`;

const EnhancedCard = styled(Card)`
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  padding: ${spacing.sm}; // Further reduced padding
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${colors.accent.main};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const CompactGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: ${spacing.sm};
  
  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: ${spacing.xs};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.sm};
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.xs};
  flex-shrink: 0;
`;

const FlashIcon = styled.div`
  width: 36px;
  height: 36px;
  background: ${colors.accent.main}20;
  color: ${colors.accent.main};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  border: 2px solid ${colors.accent.main}30;
`;

const CompactBadge = styled(Badge)`
  font-size: 10px;
  padding: 2px 6px;
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompactTitle = styled(CardTitle)`
  font-size: 14px;
  margin-bottom: ${spacing.xs};
  line-height: 1.3;
`;

const CompactDescription = styled(CardDescription)`
  font-size: 12px;
  margin-bottom: ${spacing.sm};
  line-height: 1.4;
`;

const CardMetadata = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin-top: ${spacing.xs};
`;

const MetadataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const CompanyName = styled.span`
  font-weight: 500;
  color: ${colors.text.primary};
`;

const ScanDate = styled.span`
  color: ${colors.primary.light};
`;

// Grouping Components
const GroupingControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  
  @media (max-width: ${breakpoints.mobile}) {
    gap: ${spacing.xs};
    flex-wrap: wrap;
  }
`;

const GroupingLabel = styled.span`
  color: ${colors.text.secondary};
  font-size: 14px;
  font-weight: 500;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 12px;
    white-space: nowrap;
  }
`;

const GroupingToggle = styled.div`
  display: flex;
  background: ${colors.background.tertiary};
  border-radius: 8px;
  padding: 2px;
  overflow-x: auto;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-shrink: 0;
    min-width: 0;
  }
`;

const ToggleOption = styled.button<{ active?: boolean; disabled?: boolean }>`
  background: ${props => props.active ? colors.primary.main : 'transparent'};
  color: ${props => 
    props.disabled ? colors.text.secondary + '60' :
    props.active ? 'white' : colors.text.secondary
  };
  border: none;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};
  white-space: nowrap;
  min-height: 32px;
  
  &:hover {
    color: ${props => 
      props.disabled ? colors.text.secondary + '60' :
      props.active ? 'white' : colors.text.primary
    };
    background: ${props => 
      props.disabled ? 'transparent' :
      props.active ? colors.primary.main : colors.background.tertiary
    };
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 8px 10px;
    font-size: 11px;
    min-height: 36px;
    touch-action: manipulation;
  }
`;

const GroupSection = styled.div`
  margin-bottom: ${spacing.xl};
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
  padding-bottom: ${spacing.sm};
  border-bottom: 2px solid ${colors.background.tertiary};
`;

const GroupTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const GroupCount = styled.span`
  background: ${colors.primary.main}20;
  color: ${colors.primary.main};
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 12px;
`;

const GroupIcon = styled.span`
  font-size: 18px;
`;

const ChronologicalSeparator = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  margin: ${spacing.lg} 0 ${spacing.md} 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${colors.background.tertiary};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    margin: ${spacing.md} 0 ${spacing.sm} 0;
    gap: ${spacing.xs};
  }
`;

const SeparatorTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.secondary};
  margin: 0;
  padding: 0 ${spacing.md};
  background: ${colors.background.primary};
`;

type SortOption = 'date' | 'company' | 'lastViewed';
type GroupingOption = 'chronological' | 'company' | 'year' | 'monthYear';

interface LibraryTabProps {
  files: StorageItem[];
  loading: boolean;
  error: string | null;
  getFilteredFiles: () => StorageItem[];
  handleHTMLFileClick: (item: StorageItem) => void;
}

export const LibraryTab: React.FC<LibraryTabProps> = ({
  files,
  loading,
  error,
  getFilteredFiles,
  handleHTMLFileClick
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [groupBy, setGroupBy] = useState<GroupingOption>('chronological');

  // Process and filter files with enhanced data
  const processedFiles = useMemo(() => {
    return getFilteredFiles().map(file => {
      const parsed = parseFlashCheckFileName(file.name);
      return {
        ...file,
        parsed,
        companyName: parsed?.companyName || 'Unknown Company',
        displayName: parsed?.displayName || file.name,
        scanDate: parsed?.scanDate || new Date(0),
        lastViewed: file.lastModified || new Date()
      };
    });
  }, [getFilteredFiles]);

  // Filter by search term
  const filteredFiles = useMemo(() => {
    if (!searchTerm.trim()) return processedFiles;
    
    const term = searchTerm.toLowerCase();
    return processedFiles.filter(file => 
      file.companyName.toLowerCase().includes(term) ||
      file.displayName.toLowerCase().includes(term) ||
      file.name.toLowerCase().includes(term)
    );
  }, [processedFiles, searchTerm]);

  // Sort files
  const sortedFiles = useMemo(() => {
    const sorted = [...filteredFiles];
    
    switch (sortBy) {
      case 'company':
        return sorted.sort((a, b) => a.companyName.localeCompare(b.companyName));
      case 'date':
        return sorted.sort((a, b) => b.scanDate.getTime() - a.scanDate.getTime());
      case 'lastViewed':
        return sorted.sort((a, b) => b.lastViewed.getTime() - a.lastViewed.getTime());
      default:
        return sorted;
    }
  }, [filteredFiles, sortBy]);

  // Group files based on selected grouping option
  const groupedFiles = useMemo(() => {
    if (groupBy === 'chronological') {
      // For chronological, group by month-year for separators
      const grouped = new Map<string, typeof sortedFiles>();
      
      sortedFiles.forEach(file => {
        const monthYear = `${file.parsed?.month || 'Unknown'} ${file.parsed?.year || 'Unknown'}`;
        if (!grouped.has(monthYear)) {
          grouped.set(monthYear, []);
        }
        grouped.get(monthYear)!.push(file);
      });
      
      // Sort groups by date (most recent first)
      return Array.from(grouped.entries())
        .sort(([, a], [, b]) => {
          const aDate = a[0]?.scanDate || new Date(0);
          const bDate = b[0]?.scanDate || new Date(0);
          return bDate.getTime() - aDate.getTime();
        })
        .map(([key, files]) => ({ key, files, icon: '📅' }));
    }
    
    if (groupBy === 'company') {
      const grouped = new Map<string, typeof sortedFiles>();
      
      sortedFiles.forEach(file => {
        const company = file.companyName;
        if (!grouped.has(company)) {
          grouped.set(company, []);
        }
        grouped.get(company)!.push(file);
      });
      
      return Array.from(grouped.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, files]) => ({ key, files, icon: '🏢' }));
    }
    
    if (groupBy === 'year') {
      const grouped = new Map<string, typeof sortedFiles>();
      
      sortedFiles.forEach(file => {
        const year = file.parsed?.year?.toString() || 'Unknown';
        if (!grouped.has(year)) {
          grouped.set(year, []);
        }
        grouped.get(year)!.push(file);
      });
      
      return Array.from(grouped.entries())
        .sort(([a], [b]) => b.localeCompare(a)) // Most recent year first
        .map(([key, files]) => ({ key, files, icon: '📊' }));
    }
    
    if (groupBy === 'monthYear') {
      const grouped = new Map<string, typeof sortedFiles>();
      
      sortedFiles.forEach(file => {
        const monthYear = `${file.parsed?.month || 'Unknown'} ${file.parsed?.year || 'Unknown'}`;
        if (!grouped.has(monthYear)) {
          grouped.set(monthYear, []);
        }
        grouped.get(monthYear)!.push(file);
      });
      
      return Array.from(grouped.entries())
        .sort(([, a], [, b]) => {
          const aDate = a[0]?.scanDate || new Date(0);
          const bDate = b[0]?.scanDate || new Date(0);
          return bDate.getTime() - aDate.getTime();
        })
        .map(([key, files]) => ({ key, files, icon: '📈' }));
    }
    
    return [];
  }, [sortedFiles, groupBy]);

  const renderFileCard = (item: any) => (
    <EnhancedCard
      key={item.path}
      variant="result"
      onClick={() => handleHTMLFileClick(item)}
    >
      <CardHeader>
        <IconContainer>
          <FlashIcon>⚡</FlashIcon>
          <CompactBadge variant="success">Report</CompactBadge>
        </IconContainer>
        
        <CardContent>
          <CompactTitle>{item.displayName}</CompactTitle>
          <CompactDescription>
            Click to view FlashCheck analysis
          </CompactDescription>
        </CardContent>
      </CardHeader>

      <CardMetadata>
        <MetadataRow>
          <CompanyName>🏢 {item.companyName}</CompanyName>
          <ScanDate>{formatRelativeDate(item.scanDate)}</ScanDate>
        </MetadataRow>
        
        <MetadataRow>
          <span>📅 {item.parsed?.month} {item.parsed?.year}</span>
          <span>👁️ {formatRelativeDate(item.lastViewed)}</span>
        </MetadataRow>
      </CardMetadata>
    </EnhancedCard>
  );

  const renderGroupedContent = () => {
    if (groupBy === 'chronological') {
      // Special chronological view with month separators
      return (
        <div>
          {groupedFiles.map(({ key, files }, index) => (
            <div key={key}>
              {index > 0 && (
                <ChronologicalSeparator>
                  <SeparatorTitle>{key}</SeparatorTitle>
                </ChronologicalSeparator>
              )}
              {index === 0 && (
                <ChronologicalSeparator>
                  <SeparatorTitle>📅 {key}</SeparatorTitle>
                </ChronologicalSeparator>
              )}
              <CompactGrid>
                {files.map(renderFileCard)}
              </CompactGrid>
            </div>
          ))}
        </div>
      );
    }
    
    // Regular grouped view
    return (
      <div>
        {groupedFiles.map(({ key, files, icon }) => (
          <GroupSection key={key}>
            <GroupHeader>
              <GroupIcon>{icon}</GroupIcon>
              <GroupTitle>{key}</GroupTitle>
              <GroupCount>{files.length}</GroupCount>
            </GroupHeader>
            <CompactGrid>
              {files.map(renderFileCard)}
            </CompactGrid>
          </GroupSection>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <ContentPadding>
        <EmptyState>
          <EmptyIcon>⏳</EmptyIcon>
          <EmptyTitle>Loading FlashCheck Results...</EmptyTitle>
          <EmptyDescription>Please wait while we fetch your analysis reports.</EmptyDescription>
        </EmptyState>
      </ContentPadding>
    );
  }

  if (processedFiles.length === 0) {
    return (
      <ContentPadding>
        <EmptyState>
          <EmptyIcon>📊</EmptyIcon>
          <EmptyTitle>No FlashCheck Results Yet</EmptyTitle>
          <EmptyDescription>
            Upload documents in the Admin Panel to generate FlashCheck analysis reports.
            Results will appear here once processing is complete.
          </EmptyDescription>
        </EmptyState>
      </ContentPadding>
    );
  }

  return (
    <ContentPadding>
      <LibraryHeader>
        <SearchAndFilterSection>
          <SearchContainer>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by company name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
          
          <div style={{ 
            display: 'flex', 
            gap: spacing.sm,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <GroupingControls>
              <GroupingLabel>View:</GroupingLabel>
              <GroupingToggle>
                <ToggleOption 
                  active={groupBy === 'chronological'} 
                  onClick={() => setGroupBy('chronological')}
                >
                  Timeline
                </ToggleOption>
                <ToggleOption 
                  active={groupBy === 'company'} 
                  onClick={() => setGroupBy('company')}
                >
                  Company
                </ToggleOption>
                <ToggleOption 
                  active={groupBy === 'year'} 
                  onClick={() => setGroupBy('year')}
                >
                  Year
                </ToggleOption>
                <ToggleOption 
                  active={groupBy === 'monthYear'} 
                  onClick={() => setGroupBy('monthYear')}
                >
                  Month
                </ToggleOption>
              </GroupingToggle>
            </GroupingControls>
            
            <GroupingControls>
              <GroupingLabel>Sort:</GroupingLabel>
              <GroupingToggle>
                <ToggleOption 
                  active={sortBy === 'date'} 
                  onClick={() => groupBy !== 'chronological' && setSortBy('date')}
                  disabled={groupBy === 'chronological'}
                >
                  Date
                </ToggleOption>
                <ToggleOption 
                  active={sortBy === 'company'} 
                  onClick={() => groupBy !== 'chronological' && setSortBy('company')}
                  disabled={groupBy === 'chronological'}
                >
                  Company
                </ToggleOption>
                <ToggleOption 
                  active={sortBy === 'lastViewed'} 
                  onClick={() => groupBy !== 'chronological' && setSortBy('lastViewed')}
                  disabled={groupBy === 'chronological'}
                >
                  Last Viewed
                </ToggleOption>
              </GroupingToggle>
            </GroupingControls>
          </div>
        </SearchAndFilterSection>

        <StatsContainer>
          <span>📊 {sortedFiles.length} report{sortedFiles.length !== 1 ? 's' : ''}</span>
          {searchTerm && (
            <span>🔍 Filtered from {processedFiles.length} total</span>
          )}
          <span>📂 {groupBy === 'chronological' ? 'Timeline View' : 
                    groupBy === 'company' ? 'Grouped by Company' : 
                    groupBy === 'year' ? 'Grouped by Year' : 'Grouped by Month'}</span>
        </StatsContainer>
      </LibraryHeader>

      {sortedFiles.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🔍</EmptyIcon>
          <EmptyTitle>No Results Found</EmptyTitle>
          <EmptyDescription>
            No FlashCheck reports match your search for "{searchTerm}".
            Try adjusting your search terms.
          </EmptyDescription>
        </EmptyState>
      ) : (
        renderGroupedContent()
      )}
    </ContentPadding>
  );
};
